import React, { useEffect } from 'react';
import { X, User as UserIcon, Loader2, Mail, Send } from 'lucide-react';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';

/* ═══════════════════════════════════════════════════════════════════════
   OUVRIR UN ACCÈS

   Il n'y a plus de mot de passe à générer, à copier ni à transmettre. Le
   compte s'ouvre sans mot de passe utilisable, et la personne choisit le
   sien en suivant un lien à usage unique reçu par courriel.

   Ce que cela retire du formulaire : un champ en lecture seule, un bouton
   « copier », un bouton « régénérer », et l'obligation pour celui qui crée
   le compte de faire parvenir un secret par un canal qu'il choisit — le
   plus souvent une conversation, où il reste indéfiniment.

   Ce que cela ajoute : l'adresse e-mail devient obligatoire, puisque c'est
   par elle que le lien arrive. Le formulaire le dit plutôt que de laisser
   le serveur refuser après coup.
   ═══════════════════════════════════════════════════════════════════════ */

export default function AdminForm({ newAdmin, setNewAdmin, onSubmit, onClose, saving }) {
  useEffect(() => {
    const precedent = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = precedent; };
  }, []);

  const modal = (
    <div className="admin-modal-overlay">
      <motion.form
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onSubmit={onSubmit}
        className="admin-modal-form"
      >
        <div className="admin-modal-header">
          <h2 className="titre-carte">Nouvel accès</h2>
          <button type="button" onClick={onClose} aria-label="Fermer"
                  className="act act-icone act-tertiaire">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="admin-modal-content space-y-5">
          <p className="text-[13px] text-[var(--text-muted)] leading-relaxed">
            La personne recevra un lien de première connexion et choisira
            elle-même son mot de passe. Aucun mot de passe n'est créé ici :
            vous n'aurez rien à lui transmettre.
          </p>

          <div className="space-y-1.5">
            <label htmlFor="acces-identifiant">Identifiant de connexion</label>
            <div className="relative field-with-icon">
              <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)] pointer-events-none" />
              <input
                id="acces-identifiant"
                type="text"
                required
                autoComplete="off"
                className="champ"
                placeholder="Ex : admin_01"
                value={newAdmin.username}
                onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value.trim() })}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="acces-email">Adresse e-mail</label>
            <div className="relative field-with-icon">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)] pointer-events-none" />
              <input
                id="acces-email"
                type="email"
                required
                autoComplete="off"
                className="champ"
                placeholder="prenom.nom@impactlab-cilis.org"
                value={newAdmin.email || ''}
                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value.trim() })}
              />
            </div>
            <p className="text-[12px] text-[var(--text-muted)] leading-relaxed">
              Obligatoire : c'est par cette adresse que le lien de première
              connexion est envoyé.
            </p>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="acces-role">Rôle et privilèges</label>
            <select
              id="acces-role"
              className="champ"
              value={newAdmin.role}
              onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}
            >
              <option value="SUPER_ADMIN">Super administrateur</option>
              <option value="ADMIN">Administrateur technique</option>
              <option value="ANALYSTE">Analyste (lecture seule)</option>
            </select>
          </div>
        </div>

        <div className="admin-modal-footer">
          <button type="button" onClick={onClose} className="act act-contour flex-1">
            Annuler
          </button>
          <button type="submit" disabled={saving} aria-busy={saving || undefined} className="act flex-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Send className="w-4 h-4" strokeWidth={2} />}
            Créer et envoyer le lien
          </button>
        </div>
      </motion.form>
    </div>
  );

  return createPortal(modal, document.body);
}
